import {
  isWorkspaceAgentOnboardingRole,
  isWorkspaceAgentOnboardingTimeSinkId,
  type WorkspaceAgentOnboardingRole,
  type WorkspaceAgentOnboardingTimeSinkId,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentOnboardingHandoff';
import { getWorkspaceAgentChatIdFromLocation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentChatIdFromLocation';
import { QueryParamNames } from '~/constants/search';
import { setQueryParams } from '~/utils/PathUtils';

export type WorkspaceAgentOnboardingRoleQueryValue = string | readonly string[] | null | undefined;

const readFirstQueryValue = (value: WorkspaceAgentOnboardingRoleQueryValue): string | null => {
  if (typeof value === 'string') {
    return value;
  }

  if (!value || value.length === 0) {
    return null;
  }

  return value[0] ?? null;
};

export const parseWorkspaceAgentOnboardingRoleQueryValue = (
  value: WorkspaceAgentOnboardingRoleQueryValue,
): WorkspaceAgentOnboardingRole | null => {
  const raw = readFirstQueryValue(value)?.trim();

  if (!raw || !isWorkspaceAgentOnboardingRole(raw)) {
    return null;
  }

  return raw;
};

export const parseWorkspaceAgentOnboardingTimeSinkIds = (
  value: WorkspaceAgentOnboardingRoleQueryValue,
): WorkspaceAgentOnboardingTimeSinkId[] => {
  const raw = readFirstQueryValue(value);

  if (!raw) {
    return [];
  }

  return raw.split(',').flatMap((part) => {
    const timeSinkId = part.trim();

    return isWorkspaceAgentOnboardingTimeSinkId(timeSinkId) ? [timeSinkId] : [];
  });
};

export const parseWorkspaceAgentOnboardingRoleSearch = (search: string): WorkspaceAgentOnboardingRole | null =>
  parseWorkspaceAgentOnboardingRoleQueryValue(new URLSearchParams(search).get(QueryParamNames.onboardingRole));

export const parseWorkspaceAgentOnboardingTimeSinksSearch = (search: string): WorkspaceAgentOnboardingTimeSinkId[] =>
  parseWorkspaceAgentOnboardingTimeSinkIds(new URLSearchParams(search).get(QueryParamNames.onboardingTimeSinks));

export type WorkspaceAgentOnboardingRoleHandoff = {
  role: WorkspaceAgentOnboardingRole;
  /**
   * A `chatId` already on the URL is a chat the user continued into. Resetting it would throw away
   * that thread on every refresh. The handoff only opens a fresh thread when the role param is the
   * only agent state in the URL.
   */
  shouldOpenFreshThread: boolean;
  timeSinkIds: readonly WorkspaceAgentOnboardingTimeSinkId[];
};

/** Drops the intro answers from the URL. The live run, if any, is held in memory instead. */
export const clearWorkspaceAgentOnboardingQueryParams = () => {
  setQueryParams(
    {
      [QueryParamNames.onboardingRole]: null,
      [QueryParamNames.onboardingTimeSinks]: null,
    },
    'replace',
  );
};

export const getWorkspaceAgentOnboardingRoleHandoff = (search: string): WorkspaceAgentOnboardingRoleHandoff | null => {
  const params = new URLSearchParams(search);
  const role = parseWorkspaceAgentOnboardingRoleQueryValue(params.get(QueryParamNames.onboardingRole));

  if (!role) {
    return null;
  }

  return {
    role,
    shouldOpenFreshThread: !getWorkspaceAgentChatIdFromLocation({ search }),
    timeSinkIds: parseWorkspaceAgentOnboardingTimeSinkIds(params.get(QueryParamNames.onboardingTimeSinks)),
  };
};
